/**
 * Detections-list panel — the dockable surface listing recent SAPIENT
 * detections, complementing the Map's detection glyphs (PR5b) per
 * ADR-024 §2 + the Map <-> Detections synchronized-selection edge
 * (REQ-UI-051 / ADR-024 §5). REQ-UI-061; storyboard Flow 02
 * §"Marker conventions" (detection-row storyboard section lands in PR6;
 * the row inherits the event-row conventions until then). This replaced the
 * Events panel under the ADR-032 cascade (Event removal, PR6).
 *
 * # Data
 *
 * Reads the shared :func:`detections/detections-store` buffer
 * (newest-first), which :func:`detections/use-recent-detections-feed`
 * seeds from the global ``GET /api/query/recent-detections`` (polled) and
 * :func:`detections/use-detections-sse-bridge` tops up with live in-view
 * SSE deltas. The panel mounts the feed so the list populates whenever
 * it's open; the buffer is shared with the (PR5b) Map glyph layer so both
 * surfaces show the same set.
 *
 * # Row contract
 *
 * Each row shows **time** (HH:MM:SS wall clock), **classification**
 * (``primary_class``, "Unclassified" when null), **confidence**
 * (``detection_confidence`` as a whole percent, "—" when null), and
 * **producer** (``producer_id``). Mirrors the Events row's layout sans
 * the priority dot — detections carry no priority. A row whose detection
 * has **no resolved location** (an unprojectable BSI range/bearing,
 * REQ-INGEST-044) also carries an **"approx" badge** (REQ-UI-071): the map
 * anchors such a detection faint at its reporting platform when that
 * platform's position is known, else it's list-only — either way the badge
 * flags the imprecise position at a glance (CLAUDE.md §8.1), and the state
 * is mirrored into the row's ``aria-label`` (REQ-UI-059).
 *
 * # Selection (the 2026-06-09 two-slot redesign)
 *
 *   - **Row click -> inspected.** Writes ``{kind: "detection", id}`` to
 *     the ``inspected`` slot ONLY; Entity Details populates via the detail
 *     fetch (PR5a inc 4) and this row highlights. The Map glyph ring/camera
 *     are left untouched (the intended divergence — push-to-map is the
 *     Phase-2 "Select on Map" affordance).
 *   - **Map glyph-click -> row highlight.** A Map glyph click sets BOTH
 *     slots, so the matching row here left-edge-highlights via
 *     :func:`useIsInspected`.
 *   - **Right-click / context-menu key -> "Select on Map".** Each row is
 *     wrapped with :func:`SelectOnMapContextMenu`, whose single item pushes
 *     the detection to the map (both slots + force-recenter). A detection
 *     with a null position (REQ-INGEST-044) is map-invisible, so the item
 *     is disabled with a "No map location" hint (Phase-2 affordance).
 *
 * Display-only mode (REQ-UI-056) is not yet implemented in the UI and is
 * not gated here — consistent with the Events + Platforms panels, which
 * also call ``select()`` ungated. When REQ-UI-056 lands it gates all
 * selection originators centrally.
 *
 * Cross-references:
 *   - REQ-UI-061 (Detections panel); REQ-UI-051 (synchronized selection);
 *     REQ-UI-059 (keyboard nav + accessible row name); REQ-UI-026/027.
 *   - REQ-UI-071 (range/bearing approximate-position badge, mirroring the
 *     map's platform-anchored glyph).
 *   - REQ-INGEST-044 (nullable position / confidence / classification).
 *   - ADR-024 §2 / §5; ADR-032; storyboard Flow 02.
 */
import { memo, useCallback, useMemo, useRef } from "react";
import {
  useIsInspected,
  useInspectedIdForKind,
  useMapSelectedIdForKind,
  useSelectionStore,
} from "@/selection/selection-store";
import { SelectOnMapContextMenu } from "@/selection/select-on-map-context-menu";
import {
  type PanelVisibilityApi,
  useKeepSelectedRowInView,
} from "@/selection/use-keep-selected-row-in-view";
import { useListFollow } from "@/multi-pane/list-follow-store";
import { ListFollowToggle } from "@/multi-pane/list-follow-toggle";
import { usePausedListRows } from "@/multi-pane/use-paused-list-rows";
import { type DetectionRecord, useRecentDetections } from "@/detections/detections-store";
import { useRecentDetectionsFeed } from "@/detections/use-recent-detections-feed";
import {
  classificationLabel,
  formatConfidence,
  formatDetectionClock,
} from "@/detections/detection-display";
import { usePreferences } from "@/preferences/preferences-context";
import { useProducerNames } from "@/lib/use-producer-names";
import { shortId } from "@/lib/short-id";
import { DETECTION_DEFAULT_SORT, sortDetectionRows } from "@/detections/detection-sort";
import { useObjectTrackById, useTrackIdForDetection } from "@/object-tracks/object-track-store";
import { formatMemberCount, trackMemberCount } from "@/object-tracks/track-display";

/**
 * Render the Detections-list panel.
 *
 * Mounted by :file:`@/multi-pane/panel-registry.tsx` against the
 * ``detections`` :type:`PanelId`. Full-height scrollable region; dockview
 * owns the surrounding chrome.
 */
interface DetectionsPanelProps {
  /**
   * dockview panel api, threaded by :func:`DetectionsDockPanel`. Lets the
   * keep-in-view hook re-centre the selected row when this tab is shown
   * after a Map selection made while it was hidden (REQ-UI-051; PR5b Map
   * half). Optional so the panel renders standalone in tests.
   */
  panelApi?: PanelVisibilityApi;
}

/** A detection row's stable identity for the pause snapshot. */
const detectionRowId = (detection: DetectionRecord): string => detection.report_id;

export function DetectionsPanel({ panelApi }: DetectionsPanelProps = {}): React.ReactElement {
  // Mounting the feed here seeds + refreshes the shared store while the
  // panel is open. The query state drives the first-load / error UI; the
  // rows always render from the store so a failed *refresh* keeps the
  // last-good list visible rather than blanking it.
  const feed = useRecentDetectionsFeed();
  const detections = useRecentDetections();
  // ``producer_id → display_name`` join for the row label + the platform_name
  // sort (REQ-UI-110). Falls back to the id for an unresolved producer.
  const names = useProducerNames();
  // A row left-click inspects the detection (``inspected`` slot only) —
  // Entity Details + this list's highlight move, the Map ring stays put
  // (the 2026-06-09 two-slot redesign). A second left-click on the row that
  // is already inspected deselects it — both slots clear, the camera stays
  // (REQ-UI-051 amended 2026-10-01).
  const toggleInspect = useSelectionStore((s) => s.toggleInspect);
  // Stable per panel, not per render: the rows are memoised (REQ-UI-061 under
  // track load — feature spec ``ui-track-load-headroom`` §2.2), and an inline
  // arrow here would hand every row a fresh prop on every live event and
  // defeat the memo. ``toggleInspect`` is a stable Zustand action.
  const onSelectDetection = useCallback(
    (id: string): void => toggleInspect({ kind: "detection", id }),
    [toggleInspect],
  );
  const selectedDetectionId = useInspectedIdForKind("detection");
  // The map slot too, only so a detection selected on the map is admitted to
  // a paused list and therefore has a row to reveal (REQ-UI-051).
  const mapSelectedDetectionId = useMapSelectedIdForKind("detection");
  const listRef = useRef<HTMLUListElement | null>(null);

  // The operator's per-panel sort selection (REQ-UI-110), persisted in the
  // working-state / Profile ``list_sort``; absent ⇒ the panel default (time
  // descending, newest first). The header gear writes it; this body reads the
  // SAME working state so the list re-orders live.
  const sortSelection =
    usePreferences().workingState.list_sort?.detections ?? DETECTION_DEFAULT_SORT;

  // Sort a COPY (sortBy is non-mutating, the store buffer is untouched) by the
  // selection. Recomputed only when the buffer, the name index, or the
  // selection changes. Sorting is on row identity, so it never disturbs the
  // id-keyed selection or the keep-in-view anchor (REQ-UI-051).
  const sortedDetections = useMemo(
    () => sortDetectionRows(detections, names, sortSelection),
    [detections, names, sortSelection],
  );

  // What the list renders. While the panel follows, that is the live sorted
  // rows; while it does not, the rows frozen at the flip — a 500-row ring
  // buffer turning over in under three seconds cannot be held by scrolling
  // (REQ-UI-182 amended 2026-10-10; see ``use-paused-list-rows``).
  const follow = useListFollow("detections");
  const { rows: visibleDetections, withheld } = usePausedListRows({
    rows: sortedDetections,
    follow,
    idOf: detectionRowId,
    admit: [selectedDetectionId, mapSelectedDetectionId],
  });

  // Keep the selected row visible: centre it on selection / when the tab is
  // shown, and hold it steady as detections stream in. Keyed on the RENDERED
  // list so the anchor logic sees the order the operator sees, and so a paused
  // list (which by construction does not move) runs no anchor work at all
  // (REQ-UI-051; ADR-024 §5; storyboard Flow 02 + Flow Q §"Frame 2").
  useKeepSelectedRowInView(listRef, selectedDetectionId, visibleDetections, panelApi, { follow });

  if (feed.isLoading && detections.length === 0) {
    return <LoadingState />;
  }
  if (feed.isError && detections.length === 0) {
    return <ErrorState message={feed.error.message} onRetry={() => void feed.refetch()} />;
  }
  // Gated on the rendered rows: a list paused while empty stays empty.
  if (visibleDetections.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      data-testid="detections-panel"
      role="region"
      aria-label="Recent detections"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="shrink-0 whitespace-nowrap text-body font-medium text-text-primary">
          Detections{" "}
          <span className="ml-2 text-body-sm text-text-muted">{visibleDetections.length}</span>
        </h2>
        <ListFollowToggle panelId="detections" withheld={withheld} />
      </header>
      <ul
        ref={listRef}
        // ``overscroll-contain``: a wheel that reaches this list's end must not
        // continue into dockview's own ``.dv-view`` — an ``overflow-y: auto``
        // wrapper around the whole group, tab strip included — which scrolls the
        // panel's tab up under the top nav bar while the page itself never moves
        // (measured on Selection Details 2026-10-10: ``.dv-view`` scrollTop
        // 0 → 605, group y 48 → -557). No list panel can reach that state today
        // (``.dv-view`` has no scrollable extent around one — probed in both
        // directions at two viewport heights), but the end of a list is now a
        // place operators sit: a paused list stops moving, so they scroll to the
        // bottom and keep going (REQ-UI-182 amended 2026-10-10).
        className="flex-1 overflow-y-auto overscroll-contain"
        role="list"
        aria-label="Detection list"
      >
        {visibleDetections.map((detection) => (
          <DetectionRow
            key={detection.report_id}
            detection={detection}
            platformName={names.nameFor(detection.producer_id)}
            onSelect={onSelectDetection}
          />
        ))}
      </ul>
    </div>
  );
}

interface DetectionRowProps {
  detection: DetectionRecord;
  /**
   * The detecting platform's display name (REQ-UI-110) — joined from the
   * producers inventory, or the raw ``producer_id`` when unresolved. Shown in
   * place of the raw id (which was the pre-REQ-UI-110 row content).
   */
  platformName: string;
  onSelect: (reportId: string) => void;
}

/**
 * One timeline row. Memoised: under track load the store commits on every live
 * detection and the panel re-renders, but a row whose ``detection`` record,
 * platform name and callback are unchanged must not re-render (feature spec
 * ``ui-track-load-headroom`` §2.2; the 2026-10-07 site profile re-rendered all
 * 500 rows per event). ``isSelected`` is read from the selection store inside,
 * so a selection change still re-renders exactly the rows it touches.
 */
const DetectionRow = memo(function DetectionRow({
  detection,
  platformName,
  onSelect,
}: DetectionRowProps): React.ReactElement {
  const isSelected = useIsInspected("detection", detection.report_id);
  const classification = classificationLabel(detection.primary_class);
  const confidence = formatConfidence(detection.detection_confidence);
  // ADR-041 (REQ-UI-175): the ``×N`` badge when this detection belongs to a
  // live track with two or more members — the buffered record's own
  // ``track_id`` first (an SSE frame or a REST row from a track-aware BFF),
  // else the reverse index the object-track store maintains.
  const indexedTrackId = useTrackIdForDetection(detection.report_id);
  const track = useObjectTrackById(detection.track_id !== "" ? detection.track_id : indexedTrackId);
  const trackCount = track !== null ? trackMemberCount(track) : 0;
  // A null-position detection (REQ-INGEST-044) renders no map glyph, so
  // "Select on Map" has nothing to recenter to → the item is disabled.
  const locatable = detection.latitude_deg !== null && detection.longitude_deg !== null;

  // Scroll-into-view on selection / show / data-update is handled at the
  // panel level (see DetectionsPanel) so the selected row stays anchored
  // as new detections stream in — a per-row effect keyed on ``isSelected``
  // only fires on the selection transition and can't react to list prepends.

  return (
    <SelectOnMapContextMenu
      value={{ kind: "detection", id: detection.report_id }}
      locatable={locatable}
    >
      <li
        tabIndex={0}
        role="listitem"
        aria-selected={isSelected}
        aria-label={`${classification} detection from ${platformName} at ${formatDetectionClock(detection.time)}, confidence ${confidence}${locatable ? "" : ", approximate position (range/bearing)"}${trackCount >= 2 ? `, one of ${trackCount} in its track` : ""}`}
        data-row-id={detection.report_id}
        data-testid={`detection-row-${detection.report_id}`}
        onClick={() => onSelect(detection.report_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(detection.report_id);
          }
        }}
        className={[
          "group relative cursor-pointer border-b border-border px-4 py-3",
          "transition-colors duration-100",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          isSelected ? "bg-bg-elevated" : "",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute left-0 top-0 h-full w-[3px] transition-colors duration-100",
            isSelected ? "bg-brand" : "bg-transparent",
          ].join(" ")}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className="font-mono text-body-sm text-text-secondary"
              data-testid="detection-row-time"
            >
              {formatDetectionClock(detection.time)}
            </span>
            {/* Confidence as a right-aligned percent — "—" when the source
             * omitted it (REQ-INGEST-044). */}
            <span
              className="font-mono text-body-sm text-text-secondary"
              data-testid="detection-row-confidence"
            >
              {confidence}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="text-body text-text-primary" data-testid="detection-row-class">
                {classification}
              </span>
              {/* Track-membership badge (ADR-041; REQ-UI-175): this detection is
               * one of N in a live track. aria-hidden — the row's aria-label
               * carries the count. */}
              {trackCount >= 2 && (
                <span
                  data-testid="detection-row-track-count"
                  aria-hidden="true"
                  title="Member of a live track — see the Tracks panel"
                  className="inline-flex shrink-0 items-center rounded border border-border px-1.5 py-0.5 font-mono text-caption text-text-muted"
                >
                  {formatMemberCount(trackCount)}
                </span>
              )}
            </span>
            {/* Approximate-position badge (REQ-UI-071): the detection carries an
             * unprojectable range/bearing (no resolved location, REQ-INGEST-044),
             * so the map anchors its glyph faint at the reporting platform when
             * that platform's position is known, else it's list-only. The badge
             * flags the imprecise position at a glance (CLAUDE.md §8.1); the
             * accessible state rides the row's aria-label (REQ-UI-059), so the
             * pill is aria-hidden to avoid double-announcing. */}
            {!locatable && (
              <span
                data-testid="detection-row-approximate"
                aria-hidden="true"
                title="Range/bearing — no resolved location; shown at the reporting platform on the map when its position is known (REQ-UI-071)."
                className="inline-flex shrink-0 items-center rounded border border-border px-1.5 py-0.5 text-caption font-medium uppercase tracking-wide text-text-muted"
              >
                approx
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {/* Detecting-platform DISPLAY NAME (REQ-UI-110) — joined from the
             * producers inventory; falls back to the raw producer_id when the
             * inventory hasn't (yet) resolved it. ``title`` carries the raw id
             * so the operator can still cross-reference logs. Mono kept so a
             * fallback-id row aligns with a resolved-name row. */}
            <span
              className="truncate font-mono text-body-sm text-text-muted"
              data-testid="detection-row-producer"
              title={detection.producer_id}
            >
              {platformName}
            </span>
            {/* Short, glanceable report-id handle (DET-…) as a row identifier —
             * the rows carried no id before; the full ULID rides ``title`` so
             * it stays log-cross-referenceable (REQ-UI-061; CLAUDE.md §8.1).
             * aria-hidden: the row's aria-label already names the detection, so
             * the handle would only add noise for a screen reader. */}
            <span
              className="shrink-0 font-mono text-caption text-text-muted"
              data-testid="detection-row-id"
              aria-hidden="true"
              title={detection.report_id}
            >
              {shortId(detection.report_id, "DET")}
            </span>
          </div>
        </div>
      </li>
    </SelectOnMapContextMenu>
  );
});

// ---- Render-state stubs ------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="detections-panel-loading"
      role="status"
      aria-label="Loading detections"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading detections…</p>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="detections-panel-empty"
      role="region"
      aria-label="No detections"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      {/* Storyboard Flow 02 §"Empty / no-data states". */}
      <p className="text-body text-text-primary">No detections in the last 10 minutes</p>
      <p className="text-body-sm text-text-muted">New detections appear here as they arrive.</p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="detections-panel-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load detections</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}
